import { useState } from "react"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  QuestionTextarea,
  TEXT_INPUT_STYLES,
} from "@/metrics/registry/configuration"
import { supportsAgentJudgeTools } from "@/metrics/runtime"
import {
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Trash2 } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { MetricMetadata } from "@/types/metrics/metadata"

const AGENT_JUDGE_MODELS = [
  { id: "openai:gpt-4.1-mini-2025-04-14", label: "GPT-4.1 Mini" },
  { id: "openai:gpt-4.1-2025-04-14", label: "GPT-4.1" },
] as const

const AGENT_JUDGE_TOOLS = [
  {
    id: "get_transcript",
    group: "Conversation",
    label: "Read conversation transcript",
  },
  {
    id: "search_transcript",
    group: "Conversation",
    label: "Search conversation transcript",
  },
  {
    id: "search_transcript_regex",
    group: "Conversation",
    label: "Search transcript with regex",
  },
  { id: "get_trace_spans", group: "Traces", label: "Read trace spans" },
  {
    id: "query_simulation_frames",
    group: "Structured data",
    label: "Query simulation data frames",
  },
  {
    id: "get_run_context",
    group: "Run context",
    label: "Read run and test-case context",
  },
] as const

type AgentJudgeToolId = (typeof AGENT_JUDGE_TOOLS)[number]["id"]

interface AgentJudgeMetadataEditorProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
  outputConfiguration?: React.ReactNode
  textAreaRef?: React.RefObject<HTMLTextAreaElement>
  questionPlaceholder: string
}

interface AgentJudgeReadOnlyMetadataProps {
  children: React.ReactNode
  metricRecordData: MetricRecordData
  outputConfiguration?: React.ReactNode
}

export function AgentJudgeReadOnlyMetadata({
  children,
  metricRecordData,
  outputConfiguration,
}: AgentJudgeReadOnlyMetadataProps) {
  const metadata = metricRecordData.metric_metadata ?? {}
  const modelVersion = metadata.model_version ?? AGENT_JUDGE_MODELS[0].id
  const model = AGENT_JUDGE_MODELS.find(({ id }) => id === modelVersion)
  const enabledTools = metadata.enabled_tools?.map(
    (id) => AGENT_JUDGE_TOOLS.find((tool) => tool.id === id)?.label ?? id,
  )

  return (
    <div className="space-y-4">
      {children}
      {outputConfiguration}
      <div>
        <p className="text-sm font-medium text-coval-text">Model</p>
        <p className="text-sm text-coval-text">
          {model?.label ?? `Unsupported saved model: ${modelVersion}`}
        </p>
      </div>
      {enabledTools && (
        <div>
          <p className="text-sm font-medium text-coval-text">Read-only tools</p>
          <p className="text-sm text-coval-text">
            {enabledTools.length > 0 ? enabledTools.join(", ") : "None"}
          </p>
        </div>
      )}
    </div>
  )
}

export function AgentJudgeMetadataEditor({
  metricRecordData,
  onChange,
  outputConfiguration,
  textAreaRef,
  questionPlaceholder,
}: AgentJudgeMetadataEditorProps) {
  const metadata = metricRecordData.metric_metadata ?? {}
  const modelVersion =
    typeof metadata.model_version === "string" &&
    metadata.model_version.length > 0
      ? metadata.model_version
      : AGENT_JUDGE_MODELS[0].id
  const isSupportedModel = AGENT_JUDGE_MODELS.some(
    (model) => model.id === modelVersion,
  )
  const updateMetadata = (updates: Partial<MetricMetadata>) => {
    onChange({
      ...metricRecordData,
      metric_metadata: { ...metadata, ...updates },
    })
  }

  return (
    <div className="space-y-6">
      <QuestionTextarea
        metricRecordData={metricRecordData}
        onChange={onChange}
        label="Agent Judge question"
        required
        placeholder={questionPlaceholder}
        textAreaRef={textAreaRef}
      />
      {outputConfiguration}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className={FIELD_CONTAINER_STYLES}>
          <label className={LABEL_STYLES}>Model</label>
          <Select
            value={modelVersion}
            onValueChange={(model_version) => updateMetadata({ model_version })}
          >
            <SelectTrigger aria-label="Agent Judge model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AGENT_JUDGE_MODELS.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  {model.label}
                </SelectItem>
              ))}
              {!isSupportedModel && (
                <SelectItem value={modelVersion}>
                  Unsupported saved model: {modelVersion}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <p className="mt-1.5 text-xs text-coval-text-muted">
            Agent Judge supports text evaluations only.
          </p>
        </div>
        {supportsAgentJudgeTools(metricRecordData) && (
          <AgentJudgeToolControls
            metadata={metadata}
            updateMetadata={updateMetadata}
          />
        )}
      </div>
    </div>
  )
}

interface AgentJudgeToolControlsProps {
  metadata: MetricMetadata
  updateMetadata: (updates: Partial<MetricMetadata>) => void
}

function AgentJudgeToolControls({
  metadata,
  updateMetadata,
}: AgentJudgeToolControlsProps) {
  const availableTools = AGENT_JUDGE_TOOLS
  const enabledTools = Array.isArray(metadata.enabled_tools)
    ? metadata.enabled_tools
    : availableTools.map(({ id }) => id)
  const configuredTools = enabledTools.filter(
    (tool): tool is AgentJudgeToolId =>
      availableTools.some(({ id }) => id === tool),
  )

  return (
    <div className="rounded-md border border-coval-border bg-surface-default p-3">
      <p className="text-xs font-medium text-coval-text">Read-only tools</p>
      <p className="mt-1 text-xs leading-relaxed text-coval-text-muted">
        Tools only retrieve evidence scoped to this evaluation. They cannot call
        or mutate external systems.
      </p>
      <div className="mt-3 space-y-3">
        {[...new Set(availableTools.map(({ group }) => group))].map((group) => (
          <div key={group}>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-coval-text-muted">
              {group}
            </p>
            <div className="space-y-1.5">
              {availableTools
                .filter((tool) => tool.group === group)
                .map((tool) => (
                  <label
                    key={tool.id}
                    className="flex items-center gap-2 text-sm text-coval-text"
                  >
                    <Checkbox
                      checked={configuredTools.includes(tool.id)}
                      onCheckedChange={(checked) => {
                        const enabled_tools = checked
                          ? enabledTools.includes(tool.id)
                            ? enabledTools
                            : [...enabledTools, tool.id]
                          : enabledTools.filter((id) => id !== tool.id)
                        updateMetadata({ enabled_tools })
                      }}
                    />
                    {tool.label}
                  </label>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface AgentJudgeOutputConfigurationProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export function AgentJudgeCategoricalConfiguration({
  metricRecordData,
  onChange,
}: AgentJudgeOutputConfigurationProps) {
  const [newCategory, setNewCategory] = useState("")
  const categories = metricRecordData.metric_metadata?.categories ?? []
  const trimmedNewCategory = newCategory.trim()
  const categoryHasComma = trimmedNewCategory.includes(",")

  const updateCategories = (nextCategories: string[]) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        categories: nextCategories,
      },
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <label className={LABEL_STYLES} htmlFor="agent-judge-category">
            Add Category
          </label>
          <Input
            id="agent-judge-category"
            value={newCategory}
            placeholder="Enter a category"
            onChange={(event) => setNewCategory(event.target.value)}
          />
        </div>
        <Button
          disabled={
            !trimmedNewCategory ||
            categoryHasComma ||
            categories.some(
              (category) => category.trim() === trimmedNewCategory,
            )
          }
          onClick={() => {
            updateCategories([...categories, trimmedNewCategory])
            setNewCategory("")
          }}
          className="self-end"
        >
          Add
        </Button>
      </div>
      {categoryHasComma && (
        <span className="text-sm text-coval-danger-text">
          Category labels cannot contain commas.
        </span>
      )}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <div
            key={category}
            className="flex items-center gap-1 rounded-md bg-surface-1 px-2 py-1"
          >
            <span>{category}</span>
            <Button
              icon
              variant="ghost"
              onClick={() =>
                updateCategories(
                  categories.filter((value) => value !== category),
                )
              }
              className="text-coval-text-muted hover:text-coval-danger-text"
              tooltip={`Remove ${category}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
      {categories.length < 2 && (
        <span className="text-sm text-coval-danger-text">
          Add at least two categories.
        </span>
      )}
    </div>
  )
}

export function AgentJudgeNumericalConfiguration({
  metricRecordData,
  onChange,
}: AgentJudgeOutputConfigurationProps) {
  const updateValue = (
    key: "min_value" | "max_value",
    value: number | undefined,
  ) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        [key]: value,
      },
    })
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Min Value</label>
        </div>
        <input
          type="number"
          className={TEXT_INPUT_STYLES}
          value={metricRecordData.metric_metadata?.min_value ?? ""}
          onChange={(event) =>
            updateValue(
              "min_value",
              event.target.value === ""
                ? undefined
                : Number(event.target.value),
            )
          }
          placeholder="Enter minimum value"
        />
      </div>
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Max Value</label>
        </div>
        <input
          type="number"
          className={TEXT_INPUT_STYLES}
          value={metricRecordData.metric_metadata?.max_value ?? ""}
          onChange={(event) =>
            updateValue(
              "max_value",
              event.target.value === ""
                ? undefined
                : Number(event.target.value),
            )
          }
          placeholder="Enter maximum value"
        />
      </div>
    </div>
  )
}
